import type { FormEvent } from 'react';
import { useEffect } from 'react';
import type { Section } from '../models';

type UseIndexedSectionOptions<T extends Section> = {
  section: Section;
  setSection: (section: T) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  SectionClass: new (params: any) => T;
  onIndexAdded?: (section: T, index: number) => void;
  onIndexRemoved?: (section: T, index: number) => void;
};

/**
 * Custom hook to manage indexed sections (sections with addOneIndex/removeOneIndex)
 * Handles the common pattern of:
 * - Converting a generic Section to a specific typed Section (e.g., AppliancesSection)
 * - Adding new indexes
 * - Removing indexes
 *
 * @example
 * ```tsx
 * const { onAddOne, onDeleteOne } = useIndexedSection({
 *   section,
 *   setSection,
 *   SectionClass: AppliancesSection,
 * });
 * ```
 */
export function useIndexedSection<T extends Section>({
  section,
  setSection,
  SectionClass,
  onIndexAdded,
  onIndexRemoved,
}: UseIndexedSectionOptions<T>) {
  useEffect(() => {
    if (!(section instanceof SectionClass)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const typedSection = new SectionClass((section as any).initialParams);

      if (onIndexAdded) {
        const totalIndexes = typedSection.getTotalIndexes();
        if (totalIndexes) {
          for (let i = 0; i < totalIndexes; i++) {
            onIndexAdded(typedSection, i);
          }
        }
      }

      setSection(typedSection);
    }
  }, [section, setSection, SectionClass, onIndexAdded]);

  /**
   * Adds a new index to the section
   */
  function onAddOne(): void {
    const updatedSection = section.clone() as T;
    updatedSection.addOneIndex();

    const newIndex = updatedSection.getTotalIndexes() - 1;

    if (onIndexAdded) {
      onIndexAdded(updatedSection, newIndex);
    }

    setSection(updatedSection);
  }

  /**
   * Removes an index from the section
   * Expects the event target to have a data-index attribute
   */
  function onDeleteOne(event: FormEvent<HTMLElement>): void {
    const { index } = event.currentTarget.dataset;
    const indexNumber = Number(index);

    const updatedSection = section.clone() as T;
    updatedSection.removeOneIndex(indexNumber);

    if (onIndexRemoved) {
      onIndexRemoved(updatedSection, indexNumber);
    }

    setSection(updatedSection);
  }

  /**
   * Removes an index by number (useful when you don't have an event)
   */
  function removeIndex(index: number): void {
    const updatedSection = section.clone() as T;
    updatedSection.removeOneIndex(index);

    if (onIndexRemoved) {
      onIndexRemoved(updatedSection, index);
    }

    setSection(updatedSection);
  }

  return {
    onAddOne,
    onDeleteOne,
    removeIndex,
  };
}
